import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FileText,
  Plus,
  RefreshCw,
  Trash2,
  Search,
  Eye,
  AlertTriangle
} from 'lucide-react';
import templateService from '../../services/whatsapp/templateService';
import { Template, TemplateStatus } from '../../types/models/whatsapp';
import TemplateBuilder from './templates/TemplateBuilder';
import TemplatePreview from './templates/TemplatePreview';

const STATUS_TONE: Record<TemplateStatus, string> = {
  DRAFT: 'bg-gray-100 text-gray-600',
  PENDING: 'bg-warning-50 text-warning-700',
  APPROVED: 'bg-success-50 text-success-600',
  REJECTED: 'bg-danger-50 text-danger-600',
  PAUSED: 'bg-warning-50 text-warning-700',
  DISABLED: 'bg-gray-100 text-gray-500'
};

const QUALITY_TONE: Record<string, string> = {
  GREEN: 'text-success-600',
  YELLOW: 'text-warning-600',
  RED: 'text-danger-600'
};

const formatDate = (timestamp: number): string =>
  timestamp
    ? new Date(timestamp).toLocaleString([], {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    : '—';

/**
 * Template Manager — authoring, submission and status.
 *
 * Templates are the only way to message a contact outside the 24h window, so
 * this is what unblocks broadcasts.
 */
const WhatsAppTemplates: React.FC = () => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<TemplateStatus | 'all'>('all');
  const [builderOpen, setBuilderOpen] = useState(false);
  const [previewing, setPreviewing] = useState<Template | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(
    () =>
      templateService.subscribe(
        (result) => {
          setTemplates(result);
          setLoading(false);
        },
        (err) => {
          setError(err.message);
          setLoading(false);
        }
      ),
    []
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return templates.filter((t) => {
      if (statusFilter !== 'all' && t.status !== statusFilter) return false;
      if (!term) return true;
      return (
        t.name.toLowerCase().includes(term) ||
        (t.components
          .find((c) => c.type === 'BODY')
          ?.text?.toLowerCase()
          .includes(term) ??
          false)
      );
    });
  }, [templates, search, statusFilter]);

  const handleSync = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const { synced } = await templateService.sync();
      setNotice(`Synced ${synced} template${synced === 1 ? '' : 's'} from Meta.`);
    } catch (err: any) {
      setError(err.message || 'Sync failed');
    } finally {
      setBusy(false);
    }
  }, []);

  const handleDelete = useCallback(async (name: string) => {
    setBusy(true);
    setError(null);
    try {
      const result = await templateService.remove(name);
      setConfirmDelete(null);
      if (result.note) setNotice(result.note);
    } catch (err: any) {
      setError(err.message || 'Delete failed');
    } finally {
      setBusy(false);
    }
  }, []);

  return (
    <div className="flex h-[calc(100vh-4rem)] flex-col bg-wa-bg">
      <div className="flex flex-wrap items-center gap-2 border-b border-wa-border bg-wa-surface p-3">
        <h1 className="mr-2 flex items-center gap-2 text-base font-semibold text-wa-text">
          <FileText className="h-5 w-5" />
          Template Manager
          <span className="rounded-full bg-wa-surface2 px-2 py-0.5 text-xs font-normal text-wa-muted">
            {filtered.length}
          </span>
        </h1>

        <div className="relative min-w-[14rem] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-wa-muted" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or body"
            className="w-full rounded-lg border-wa-border bg-wa-surface2 pl-9 text-sm text-wa-text focus:border-wa-green focus:ring-wa-green"
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) =>
            setStatusFilter(e.target.value as TemplateStatus | 'all')
          }
          className="rounded-lg border-wa-border bg-wa-surface2 text-xs text-wa-text focus:border-wa-green focus:ring-wa-green"
        >
          <option value="all">All statuses</option>
          <option value="APPROVED">Approved</option>
          <option value="PENDING">Pending</option>
          <option value="REJECTED">Rejected</option>
          <option value="PAUSED">Paused</option>
          <option value="DISABLED">Disabled</option>
        </select>

        <button
          type="button"
          onClick={handleSync}
          disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-lg border border-wa-border px-3 py-1.5 text-xs font-medium text-wa-text hover:bg-wa-surface2 disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${busy ? 'animate-spin' : ''}`} />
          Sync from Meta
        </button>

        <button
          type="button"
          onClick={() => setBuilderOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-lg bg-wa-green px-3 py-1.5 text-xs font-medium text-white hover:bg-wa-teal"
        >
          <Plus className="h-3.5 w-3.5" />
          New template
        </button>
      </div>

      {notice && (
        <div className="flex items-center justify-between bg-primary-50 px-4 py-2 text-sm text-primary-700">
          {notice}
          <button
            type="button"
            onClick={() => setNotice(null)}
            className="font-medium underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {error && (
        <div className="flex items-center justify-between bg-danger-50 px-4 py-2 text-sm text-danger-600">
          {error}
          <button
            type="button"
            onClick={() => setError(null)}
            className="font-medium underline"
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-auto">
        <table className="w-full border-collapse text-sm">
          <thead className="sticky top-0 z-10 bg-wa-surface2">
            <tr className="text-left text-xs font-medium text-wa-muted">
              <th className="px-3 py-2.5">Name</th>
              <th className="px-3 py-2.5">Category</th>
              <th className="px-3 py-2.5">Language</th>
              <th className="px-3 py-2.5">Status</th>
              <th className="px-3 py-2.5">Quality</th>
              <th className="px-3 py-2.5">Created</th>
              <th className="px-3 py-2.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-wa-muted">
                  Loading templates…
                </td>
              </tr>
            )}

            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-12 text-center">
                  <FileText className="mx-auto h-10 w-10 text-wa-muted opacity-40" />
                  <p className="mt-2 text-sm font-medium text-wa-muted">
                    {templates.length === 0 ? 'No templates yet' : 'No matches'}
                  </p>
                  <p className="mt-1 text-xs text-wa-muted opacity-70">
                    {templates.length === 0
                      ? 'Create one, or sync templates you already have in Meta.'
                      : 'Try a different search or filter.'}
                  </p>
                </td>
              </tr>
            )}

            {!loading &&
              filtered.map((template) => (
                <tr
                  key={template.id}
                  className="border-b border-wa-border hover:bg-wa-surface2/50"
                >
                  <td className="px-3 py-2.5">
                    <span className="block font-mono text-xs font-medium text-wa-text">
                      {template.name}
                    </span>
                    {template.rejectionReason && (
                      <span className="mt-0.5 flex items-center gap-1 text-[11px] text-danger-600">
                        <AlertTriangle className="h-3 w-3 shrink-0" />
                        {template.rejectionReason}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-wa-muted">
                    {template.category}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-wa-muted">
                    {template.language}
                  </td>
                  <td className="px-3 py-2.5">
                    <span
                      className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                        STATUS_TONE[template.status]
                      }`}
                    >
                      {template.status}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    {template.qualityScore ? (
                      <span
                        className={`text-xs font-medium ${
                          QUALITY_TONE[template.qualityScore] || 'text-wa-muted'
                        }`}
                      >
                        {template.qualityScore}
                      </span>
                    ) : (
                      <span className="text-xs text-wa-muted">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-wa-muted">
                    {formatDate(template.createdAt)}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => setPreviewing(template)}
                        className="rounded p-1.5 text-wa-muted hover:bg-wa-surface2 hover:text-wa-text"
                        title="Preview"
                        aria-label={`Preview ${template.name}`}
                      >
                        <Eye className="h-4 w-4" />
                      </button>

                      {confirmDelete === template.name ? (
                        <span className="flex items-center gap-1">
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => handleDelete(template.name)}
                            className="rounded bg-danger-600 px-2 py-1 text-[11px] font-medium text-white disabled:opacity-50"
                          >
                            Confirm
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmDelete(null)}
                            className="rounded px-2 py-1 text-[11px] text-wa-muted hover:text-wa-text"
                          >
                            Cancel
                          </button>
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setConfirmDelete(template.name)}
                          className="rounded p-1.5 text-wa-muted hover:bg-danger-50 hover:text-danger-600"
                          title="Delete"
                          aria-label={`Delete ${template.name}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <TemplateBuilder
        open={builderOpen}
        onClose={() => setBuilderOpen(false)}
        onCreated={() => {
          setBuilderOpen(false);
          setNotice('Submitted to Meta. Status updates as the review completes.');
        }}
      />

      {previewing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md overflow-hidden rounded-xl bg-wa-surface shadow-2xl">
            <div className="flex items-center justify-between border-b border-wa-border px-4 py-3">
              <h2 className="font-mono text-sm font-semibold text-wa-text">
                {previewing.name}
              </h2>
              <button
                type="button"
                onClick={() => setPreviewing(null)}
                className="rounded px-2 py-1 text-xs text-wa-muted hover:text-wa-text"
              >
                Close
              </button>
            </div>
            <div className="p-4">
              <TemplatePreview
                components={previewing.components}
                examples={
                  previewing.components.find((c) => c.type === 'BODY')?.example
                    ?.body_text?.[0] || []
                }
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsAppTemplates;
